import DOMPurify from 'dompurify';
import useTranslation from 'next-translate/useTranslation';

import slugify from '@sindresorhus/slugify';

import { useUser } from '@itp/talent';

import type { NotificationType } from './types';

export const useNotificationContent = (notification: NotificationType) => {
  const { t } = useTranslation('notifications');
  const { user } = useUser();
  const isAdmin = user?.isAdmin;
  const eventData = notification.event.eventData as any;
  let type = `${eventData?.model}-${eventData?.operation}`;
  let params: Record<string, number | string> = { count: 1 };

  if (eventData?.model === 'talentJobApplication') {
    const jobAd = eventData?.result?.jobAd;
    const jobSlug = jobAd?.id ? `${jobAd.id}--${slugify(jobAd.title || 'job')}` : '';
    const talentProfile = eventData?.result?.talentProfile;
    const profileName =
      talentProfile?.profileName ||
      notification.from?.profile?.profileName ||
      notification.from?.displayName ||
      'Someone';
    const profileSlug = talentProfile?.id ? `${talentProfile.id}/${slugify(profileName)}` : '';
    const applicant = profileSlug
      ? `<a href="/talent/profile-sum/${profileSlug}">${profileName}</a>`
      : profileName;
    const jobTitle = jobAd?.title || 'Untitled Job';
    const jobLink = jobSlug ? `<a href="/job-apply/${jobSlug}">${jobTitle}</a>` : jobTitle;
    const stepName = eventData?.result?.companyHiringStep?.stepName;

    params = {
      ...params,
      applicant,
      jobLink,
      jobSlug,
      jobTitle,
      profileName,
      profileSlug,
      ...(stepName ? { stepName } : {}),
    };
  } else if (eventData?.model === 'user') {
    let displayName =
      eventData?.result?.displayName || eventData?.context?.data?.displayName || 'Someone';
    const email = eventData?.result?.email || '';

    if (!displayName || displayName === email) {
      type = 'user-created-no-name';
      displayName = email;
    }

    params = {
      ...params,
      displayName,
      email,
    };
  } else if (eventData?.model === 'talentInvitation') {
    const company = eventData?.result?.company;
    const jobAd = eventData?.result?.jobAd;
    const talentProfile = eventData?.result?.talentProfile;
    const jobTitle = jobAd?.title || 'Untitled Job';
    const jobId = eventData?.result?.jobAdId;
    const jobSlug = jobId ? `${jobId}--${slugify(jobTitle)}` : '';
    const inviterName =
      notification.from?.profile?.profileName || notification.from?.displayName || 'Someone';
    const inviterSlug = notification.from?.profile?.id
      ? `${notification.from.profile.id}/${slugify(inviterName)}`
      : '';
    const inviter = inviterSlug
      ? `<a href="/talent/profile-sum/${inviterSlug}">${inviterName}</a>`
      : inviterName;
    const companyName = company?.name || 'a company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const talentName = talentProfile?.profileName || 'a talent';
    const talentSlug = talentProfile?.id ? `${talentProfile.id}/${slugify(talentName)}` : '';
    const jobLink = jobSlug ? `<a href="/job-apply/${jobSlug}">${jobTitle}</a>` : jobTitle;

    if (isAdmin && eventData?.operation === 'created') {
      type = 'talentInvitation-created-admin';
      const talentLink = talentSlug
        ? `<a href="/talent/profile-sum/${talentSlug}">${talentName}</a>`
        : talentName;
      params = {
        ...params,
        companyName,
        companySlug,
        inviter,
        inviterName,
        inviterSlug,
        jobLink,
        jobSlug,
        jobTitle,
        talentLink,
        talentName,
      };
    } else {
      params = {
        ...params,
        companyName,
        companySlug,
        inviter,
        jobLink,
        jobSlug,
        jobTitle,
      };
    }
  } else if (eventData?.model === 'company') {
    const companyName = eventData?.result?.name || 'a company';
    const companyId = eventData?.result?.id;
    const companySlug = companyId ? `${companyId}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
    };
  } else if (eventData?.model === 'companyHiringStep') {
    const stepName = eventData?.result?.stepName || 'Hiring Step';
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
      stepName,
    };
  } else if (eventData?.model === 'companyOnUser') {
    const userName =
      eventData?.result?.user?.displayName || eventData?.result?.user?.email || 'Someone';
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;
    const roleName = eventData?.result?.role?.role;

    if (roleName && eventData?.operation === 'created') {
      type = 'companyOnUser-created-role';
    }

    params = {
      ...params,
      companyLink,
      companyName,
      ...(roleName ? { roleName } : {}),
      userName,
    };
  } else if (eventData?.model === 'companyPermissions') {
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
    };
  } else if (eventData?.model === 'companyRole') {
    const roleName = eventData?.result?.role || 'Role';
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
      roleName,
    };
  } else if (eventData?.model === 'companySavedTalent') {
    const userName = eventData?.result?.user?.displayName || 'Someone';
    const talentProfile = eventData?.result?.talentProfile;
    const talentName = talentProfile?.profileName || 'a talent';
    const talentSlug = talentProfile?.id ? `${talentProfile.id}/${slugify(talentName)}` : '';
    const talentLink = talentSlug
      ? `<a href="/talent/profile-sum/${talentSlug}">${talentName}</a>`
      : talentName;
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
      talentLink,
      talentName,
      userName,
    };
  } else if (eventData?.model === 'companyTalentNote') {
    const userName = eventData?.result?.user?.displayName || 'Someone';
    const talentProfile = eventData?.result?.talentProfile;
    const talentName = talentProfile?.profileName || 'a talent';
    const talentSlug = talentProfile?.id ? `${talentProfile.id}/${slugify(talentName)}` : '';
    const talentLink = talentSlug
      ? `<a href="/talent/profile-sum/${talentSlug}">${talentName}</a>`
      : talentName;
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
      talentLink,
      talentName,
      userName,
    };
  } else if (eventData?.model === 'companyTalentRoster') {
    const talentProfile = eventData?.result?.talentProfile;
    const talentName = talentProfile?.profileName || 'a talent';
    const talentSlug = talentProfile?.id ? `${talentProfile.id}/${slugify(talentName)}` : '';
    const talentLink = talentSlug
      ? `<a href="/talent/profile-sum/${talentSlug}">${talentName}</a>`
      : talentName;
    const company = eventData?.result?.company;
    const companyName = company?.name || 'the company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;
    const employmentType = eventData?.result?.employmentType || 'employee';

    params = {
      ...params,
      companyLink,
      companyName,
      employmentType,
      talentLink,
      talentName,
    };
  } else if (eventData?.model === 'jobAd') {
    const jobTitle = eventData?.result?.title || 'Untitled Job';
    const jobId = eventData?.result?.id;
    const jobSlug = jobId ? `${jobId}--${slugify(jobTitle)}` : '';
    const jobLink = jobSlug ? `<a href="/job-apply/${jobSlug}">${jobTitle}</a>` : jobTitle;
    const company = eventData?.result?.publisherCompany;
    const companyName = company?.name || 'a company';
    const companySlug = company?.id ? `${company.id}--${slugify(companyName)}` : '';
    const companyLink = companySlug
      ? `<a href="/company/${companySlug}">${companyName}</a>`
      : companyName;

    params = {
      ...params,
      companyLink,
      companyName,
      jobLink,
      jobTitle,
    };
  } else if (eventData?.model === 'jobAssignment') {
    const jobTitle = eventData?.result?.jobDescription?.title || 'Untitled Job';
    const clientCompanyName = eventData?.result?.clientCompany?.name || 'a company';
    const salesCompanyName = eventData?.result?.salesCompany?.name;
    const providerCompanyName = eventData?.result?.providerCompany?.name;

    params = {
      ...params,
      clientCompanyName,
      jobTitle,
      ...(providerCompanyName ? { providerCompanyName } : {}),
      ...(salesCompanyName ? { salesCompanyName } : {}),
    };
  } else if (eventData?.model === 'talentProfile') {
    const profileName = eventData?.result?.profileName || 'a talent';
    const profileId = eventData?.result?.id;
    const profileSlug = profileId ? `${profileId}/${slugify(profileName)}` : '';
    const profileLink = profileSlug
      ? `<a href="/talent/profile-sum/${profileSlug}">${profileName}</a>`
      : profileName;
    const verificationStatus = eventData?.result?.verificationStatus;
    const verificationStatusChanged = eventData?.context?.data?.verificationStatus !== undefined;

    if (verificationStatus && verificationStatusChanged && eventData?.operation === 'updated') {
      type = 'talentProfile-updated-verification';
      params = {
        ...params,
        profileLink,
        profileName,
        verificationStatus,
      };
    } else if (eventData?.operation === 'created') {
      type = 'talentProfile-created';
      params = {
        ...params,
        profileLink,
        profileName,
      };
    } else {
      type = 'talentProfile-updated';
      params = {
        ...params,
        profileLink,
        profileName,
      };
    }
  }

  const content = DOMPurify.sanitize(t(type, params));
  return content;
};
